import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Zap, Eye, Shield, ArrowRight } from 'lucide-react';
import { values, infoPoints, contactData } from '../data/mock';

const AboutPage = () => {
  const getValueIcon = (iconName) => {
    const icons = {
      'target': Target,
      'zap': Zap,
      'eye': Eye,
      'shield': Shield
    };
    return icons[iconName] || Target;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactData.whatsapp}`, '_blank');
  };

  return (
    <div style={{ background: '#000000', position: 'relative' }}>
      <div className="pattern-overlay" />
      
      {/* Hero */}
      <section style={{ paddingTop: '140px', paddingBottom: '80px', position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="display-huge" style={{ 
              marginBottom: '24px',
              animation: 'fade-in-up 0.8s ease-out'
            }}>
              Gutachten, die du <span style={{ color: '#00A8CC' }}>sofort verwenden</span> kannst.
            </h1>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '40px',
              animation: 'fade-in-up 0.8s ease-out 0.2s both'
            }}>
              Schnell vor Ort, sauber dokumentiert, rechtssicher aufbereitet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" style={{
              animation: 'fade-in-up 0.8s ease-out 0.4s both'
            }}>
              <Link to="/kontakt" className="btn-primary">
                Kontakt
              </Link>
              <Link to="/dienstleistungen" className="btn-secondary">
                Dienstleistungen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Werte */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1400px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="display-large" style={{ marginBottom: '16px' }}>
              Unsere Werte
            </h2>
            <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Was uns antreibt und auszeichnet
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = getValueIcon(value.icon);
              return (
                <div 
                  key={value.id}
                  className="glass-card"
                  style={{
                    textAlign: 'center',
                    animation: `fade-in-up 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  <div style={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '50%',
                    background: 'rgba(0, 168, 204, 0.1)',
                    border: '1px solid rgba(0, 168, 204, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px'
                  }}>
                    <Icon size={32} style={{ color: '#00A8CC' }} />
                  </div>
                  <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                    {value.title}
                  </h3>
                  <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info Panels */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '1200px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="display-large" style={{ marginBottom: '16px' }}>
              Warum ein unabhängiges Gutachten?
            </h2>
            <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Diese Punkte werden sorgfältig dokumentiert
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {infoPoints.map((point, index) => (
              <div 
                key={point.id}
                className="glass-card"
                style={{
                  animation: `fade-in-up 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                <div style={{
                  width: '48px',
                  height: '4px',
                  background: 'linear-gradient(90deg, #00A8CC, transparent)',
                  marginBottom: '24px',
                  borderRadius: '2px'
                }} />
                <h3 className="heading-1" style={{ marginBottom: '12px' }}>
                  {point.title}
                </h3>
                <p className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {point.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pad-xlarge" style={{ position: 'relative' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: '900px' }}>
          <div className="glass-card" style={{
            textAlign: 'center',
            padding: '60px 40px',
            background: 'rgba(0, 168, 204, 0.05)',
            border: '1px solid rgba(0, 168, 204, 0.2)'
          }}>
            <h2 className="display-large" style={{ marginBottom: '20px' }}>
              Jetzt Schaden prüfen lassen
            </h2>
            <p className="body-large" style={{ 
              color: 'rgba(255, 255, 255, 0.7)',
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto 40px'
            }}>
              Wir sind für Sie da – schnell und zuverlässig.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt" className="btn-primary">
                Kontakt aufnehmen
                <ArrowRight size={20} />
              </Link>
              <button onClick={handleWhatsApp} className="btn-secondary">
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;